import React, { useCallback, useEffect, useMemo, useState } from "react";
import { COLUMNS } from "./../table/customertable/debtortable/Columns";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import CommonModal from "./CommonModal";
import { buttonName, dashboard, toolTipContent } from "../../locales/en";
import Modalstyle from "./modal.module.scss";
import ErrorBoundary from "../error-boundary";
import ModalFallback from "../error-boundary/fallback/ModalFallback";
import { getDebtorInvoiceAuditLogs } from "../../redux/dashboardSlice";

import {
  useTable,
  useRowSelect,
  useExpanded,
  usePagination
} from "react-table";
import DebtorTable from "../table/customertable/debtortable/DebtorTable";
import { PAGE_INDEX, PAGE_SIZE } from "../../utils/constants/appConstants";

const DebtorPaymentDetailsModal = ({
  isOpen,
  toggle,
  invoiceIdsListRef,
  debtorName,
  invoiceDtailsPage = false,
  invData
}) => {
  const loading = useSelector((state) => state.dashboardTable.modalLoader);
  const debtorPaymentDetails = useSelector(
    (state) => state.dashboardTable.debtorPaymentDetails
  );

  const [lastDebtorRow, setLastDebtorRow] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  const dispatch = useDispatch();
  const rowData = invoiceIdsListRef?.current;

  // Memoize columns
  const columns = useMemo(() => COLUMNS, []);

  // Memoize data to prevent re-renders
  const data = useMemo(
    () => debtorPaymentDetails?.debtorList || [],
    [debtorPaymentDetails?.debtorList]
  );

  // Memoize pageCount based on totalPages
  const pageCount = useMemo(() => Number(totalPages) || 1, [totalPages]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    state: { pageIndex }
  } = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: Number(pageCount) || 1,
      autoResetPage: false,
      initialState: { pageIndex: PAGE_INDEX, pageSize: PAGE_SIZE }
    },
    useExpanded,
    usePagination,
    useRowSelect
  );

  const handleDebtorClick = useCallback(
    async (row, isRefreshed = false, pageNumber, size) => {
      if (!row) return;
      setLastDebtorRow(row);

      const source = invoiceDtailsPage ? invData?.invoice : row.original;

      if (!source) return;

      try {
        const payload = {
          userAccountId: source.userAccountId,
          debtorId: source.contactUuid || source.invoice?.contactUuid,
          quoteId: source.quoteId || source.id,
          isRefreshed,
          page: pageNumber,
          size
        };

        const response = await dispatch(
          getDebtorInvoiceAuditLogs(payload)
        ).unwrap();

        if (response) {
          const total = response?.data?.pageInfo?.totalPages;
          setTotalPages((prev) => (prev !== total ? total : prev));
        }
      } catch {
        // not required to show error message
      }
    },
    [dispatch]
  );

  // Only call handleDebtorClick when modal opens
  useEffect(() => {
    if (isOpen && rowData) {
      handleDebtorClick(rowData, false, pageIndex, pageSize);
    }
  }, [isOpen, rowData, handleDebtorClick, pageIndex, pageSize]);

  const tableProps = {
    loading,
    headerGroups,
    rows,
    getTableProps,
    getTableBodyProps,
    prepareRow,
    columns,
    tableData: data,
    theadData: debtorPaymentDetails,
    visibleColumnCount: columns.length,

    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageIndex,
    pageCount,
    pageOptions,
    pageSize,
    setPageSize
  };

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={toggle}
      title={
        <>
          {dashboard?.PaymentHistoryModal?.title}
          <br />
          <small>{debtorName}</small>
        </>
      }
      modalClassName="modal-xl"
      bodyClassName={Modalstyle.refMonthlySingleContent}
      footerButtons={[
        {
          label: buttonName.close,
          className: "btn btn-secondary",
          onClick: () => toggle(false)
        },
        {
          label: loading ? buttonName?.loading : toolTipContent?.reload,
          className: "btn btn-primary",
          onClick: () =>
            handleDebtorClick(
              lastDebtorRow || rowData || invoiceDtailsPage,
              true
            )
        }
      ]}
    >
      <ErrorBoundary fallback={<ModalFallback />}>
        <div className="row">
          <div className="col-md-12">
            <div className="table-responsive">
              <DebtorTable {...tableProps} />
            </div>
          </div>
        </div>
      </ErrorBoundary>
    </CommonModal>
  );
};

DebtorPaymentDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  debtorName: PropTypes.string,
  invoiceDtailsPage: PropTypes.bool,
  invoiceIdsListRef: PropTypes.object,
  invData: PropTypes.object
};

export default DebtorPaymentDetailsModal;
